import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { isClientAuthDebug, clientAuthLog } from './lib/debug';


export default function SsoReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();

  const returnTo = useMemo((): string | undefined => {
    try {
      const params = new URLSearchParams(location.search);
      const val = params.get('return_to');
      return val || undefined;
    } catch { return undefined; }
  }, [location.search]);

  useEffect(() => {
    const performRedirect = () => {
      if (!returnTo) {
        navigate('/', { replace: true });
        return;
      }
      // A same-origin app path (the deep link the user originally requested) — just
      // navigate there in the SPA.
      let isAbsolute = true;
      try { new URL(returnTo); } catch { isAbsolute = false; }
      if (!isAbsolute) {
        navigate(returnTo, { replace: true });
        return;
      }
      // Any absolute/cross-origin URL is untrusted. The only consumer that ever
      // needed a top-level cross-origin bounce (the Salesforce embed integration)
      // has been removed, so honoring one here would only be an open-redirect /
      // session-token-exfil vector via a crafted `?return_to=` link.
      if (isClientAuthDebug()) clientAuthLog('sso_return.untrusted_return_to_origin', { returnTo });
      navigate('/', { replace: true });
    };

    // Wait for Clerk to finalize the session cookie on our origin before leaving
    // to avoid racing the cookie write. If already signed in, go immediately.
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (isLoaded) {
      if (isSignedIn) {
        performRedirect();
      } else {
        timer = setTimeout(() => {
          // If the user became signed in meanwhile, skip the fallback redirect
          if (isSignedIn) return;
          if (isClientAuthDebug()) clientAuthLog('sso_return.timeout_waiting_session');
          // Carry the original deep link forward so a retry from /sign-in doesn't
          // lose it — only for same-origin app paths, matching SignIn's `loc.state.from`.
          let from: { pathname: string; search: string; hash: string } | undefined;
          if (returnTo) {
            try {
              new URL(returnTo);
            } catch {
              try {
                const u = new URL(returnTo, window.location.origin);
                from = { pathname: u.pathname, search: u.search, hash: u.hash };
              } catch {}
            }
          }
          try { navigate('/sign-in?reason=external_not_found', { replace: true, state: from ? { from } : undefined }); } catch {}
        }, 1200);
      }
    }

    return () => { if (timer) clearTimeout(timer); };
  }, [isLoaded, isSignedIn, navigate, returnTo]);

  return (
    <div className="min-h-screen flex items-center justify-center text-content-tertiary">
      Completing sign in...
    </div>
  );
}


